import { ANALYTICS_CONFIG } from '@/config/analytics';

/**
 * Declarative Google Analytics 4 component.
 * Renders script tags directly — no useEffect or document manipulation.
 * Page view tracking is handled via the trackPageView utility in config/analytics.ts
 */
export function GoogleAnalytics() {
  if (!ANALYTICS_CONFIG.googleAnalytics.enabled) return null;

  const { measurementId } = ANALYTICS_CONFIG.googleAnalytics;

  const initScript = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;

  return (
    <>
      <script
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
      />
      <script dangerouslySetInnerHTML={{ __html: initScript }} />
    </>
  );
}

/**
 * Declarative Google AdSense loader.
 */
export function GoogleAdSense() {
  if (!ANALYTICS_CONFIG.googleAdSense.enabled) return null;

  return (
    <script
      async
      src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ANALYTICS_CONFIG.googleAdSense.clientId}`}
      crossOrigin="anonymous"
    />
  );
}

/**
 * Declarative Microsoft Clarity component.
 */
export function MicrosoftClarity() {
  if (!ANALYTICS_CONFIG.microsoftClarity.enabled) return null;

  const clarityScript = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${ANALYTICS_CONFIG.microsoftClarity.projectId}");
  `;

  return <script dangerouslySetInnerHTML={{ __html: clarityScript }} />;
}

/**
 * AdSense ad unit — pure presentational.
 */
interface AdUnitProps {
  adSlot: string;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  style?: React.CSSProperties;
  className?: string;
}

export function AdUnit({ adSlot, adFormat = 'auto', style, className }: AdUnitProps) {
  if (!ANALYTICS_CONFIG.googleAdSense.enabled) return null;

  return (
    <>
      <ins
        className={`adsbygoogle ${className || ''}`}
        style={{ display: 'block', ...style }}
        data-ad-client={ANALYTICS_CONFIG.googleAdSense.clientId}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive="true"
      />
      <script dangerouslySetInnerHTML={{ __html: '(window.adsbygoogle = window.adsbygoogle || []).push({});' }} />
    </>
  );
}

/** Combined analytics — render once in RootLayout */
export function Analytics() {
  return (
    <>
      <GoogleAnalytics />
      <GoogleAdSense />
      <MicrosoftClarity />
    </>
  );
}

// Type declarations
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    adsbygoogle: any[];
  }
}
